/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import {
  IonButtons,
  IonRouterLink,
  useIonModal,
  useIonToast,
} from '@ionic/react'
import { format, formatISO } from 'date-fns'

import { BackButton } from '@components/atoms/BackButton'
import { Title } from '@components/atoms/Title'
import { ShareButton } from '@components/molecules/ShareButton'
import { ContestDetails } from '@components/organisms/ContestDetails'
import { ContestUploadVideo } from '@components/organisms/ContestUploadVideo'
import { Main } from '@components/templates/main'
import { Share } from '@/components/atoms/assets/Share'
import { Loading } from '@/components/atoms/Loading'
import { UserName } from '@/components/atoms/UserName'
import { CommentsButton } from '@/components/molecules/CommentsButton'
import { LeaderboardButton } from '@/components/molecules/LeaderboardButton'
import { Popover } from '@/components/molecules/Popover'

import { appConfig } from '@services/config'
import { useContestDetails } from '@/lib/hooks/useContestDetails'
import { useContestInfo } from '@/lib/hooks/useContestInfo'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { Tracking, TrackingEvent } from '@/lib/tracking'

import { ContestSections, ContestStatus, Pages } from '@/enums'

import { videoEditor } from '@/plugins/video-editor'

const minAllowedVideoDuration = 5

export const ContestDetailsPage: FC<RouteComponentProps> = ({
  match,
  history,
}: RouteComponentProps<{ contestId: string }>) => {
  const videoRef = useRef(null)
  const {
    params: { contestId },
  } = match
  const [popoverState, setPopoverState] = useState({
    isOpen: false,
    header: null,
    message: null,
    withIcon: false,
  })
  const [duration, setDuration] = useState(0)
  const { data: user } = useUserInfo()
  const [videoDataUrl, setVideoDataUrl] = useState<string>(undefined)
  const [thumbnailUrl, setThumbnailUrl] = useState<string>(undefined)
  const [thumbnailOffset, setThumbnailOffset] = useState<number>(0)
  const [videoDuration, setVideoDuration] = useState<number>(0)
  const [isJoinModalCompleted, setIsJoinModalCompleted] = useState(false)

  const [presentToast] = useIonToast()
  const {
    data: contestData,
    isError,
    isLoading,
    refetch,
  } = useContestInfo(contestId)

  const { t } = useTranslation('common')

  const {
    contestDetails,
    hasNextPage,
    fetchNextPage,
    refetchFirstPage,
    isLoading: isLoadingContestDetails,
  } = useContestDetails({ contestId, limit: 12 })

  const trackChallengeProperties = useMemo(() => {
    return {
      challengeName: contestData?.contest?.name,
      challengeId: contestId,
    }
  }, [contestData?.contest?.name, contestId])

  const onJoin = () => {
    refetchAll()
    // At present, it does not support opening in that way, and add mediaSource later
    Tracking.triggerEvent(TrackingEvent.ChallengeJoinCompleted, {
      ...trackChallengeProperties,
    })

    presentToast({
      message: t('contest.details.videoUploadedAlert.toast'),
      position: 'top',
      buttons: [
        {
          text: t('ok'),
          role: 'cancel',
        },
      ],
    })
  }

  const onContestJoinModalComplete = () => {
    setPopoverState({
      isOpen: true,
      header: t('contest.details.videoUploadedAlert.header'),
      message: t('contest.details.videoUploadedAlert.message'),
      withIcon: false,
    })
    setIsJoinModalCompleted(true)
    closeContestJoinModal()
  }

  const [openContestJoinModal, closeContestJoinModal] = useIonModal(
    ContestUploadVideo,
    {
      onComplete: () => onJoin(),
      onDismiss: () => closeContestJoinModal(),
      videoDataUrl,
      thumbnailUrl,
      thumbnailOffset,
      duration: videoDuration,
      maxVideoDuration: contestData?.contest?.videoLengthLimit,
      contestId,
      user,
      onModalComplete: () => onContestJoinModalComplete(),
    }
  )

  useEffect(() => {
    if (videoDataUrl && !!duration) {
      if (duration < minAllowedVideoDuration) {
        closeContestJoinModal()
        setDuration(0)
        return
      }
      if (!user) {
        setPopoverState({
          isOpen: true,
          header: t('contest.details.noUserDataAlert.header'),
          message: t('contest.details.noUserDataAlert.message'),
          withIcon: false,
        })
        return
      }
    }
  }, [closeContestJoinModal, duration, t, user, videoDataUrl])

  const durationChangeHandler = (e) => {
    const video = e.target as HTMLVideoElement
    setDuration(video?.duration)
    if (video?.duration > contestData?.contest?.videoLengthLimit) {
      setPopoverState({
        isOpen: true,
        header: t('contest.details.videoDurationAlert.header'),
        message: t('contest.details.videoDurationAlert.message', {
          allowedDuration: contestData?.contest?.videoLengthLimit,
        }),
        withIcon: false,
      })
    }
    if (video?.duration < minAllowedVideoDuration) {
      setPopoverState({
        isOpen: true,
        header: null,
        message: t('contest.videoTooShort.description', {
          time: minAllowedVideoDuration,
        }),
        withIcon: true,
      })
    }
  }

  const isJoined = isJoinModalCompleted || contestData?.participant

  const onJoinContest = async () => {
    const isCreator = user?.id === contestData.contest.userId

    if (
      ![ContestStatus.Submission, ContestStatus.SubmissionVoting].includes(
        contestData?.contest?.status
      )
    ) {
      setPopoverState({
        isOpen: true,
        header: null,
        message: t('contest.details.submissionNotAcceptedAlert.message'),
        withIcon: true,
      })

      return
    }

    if (isJoined || isCreator) {
      setPopoverState({
        isOpen: true,
        header: t(
          `contest.details.${
            isCreator ? 'creatorContestAlert' : 'alreadyJoinedAlert'
          }.header`,
          {
            contest: contestData.contest?.name,
          }
        ),
        message: t(
          `contest.details.${
            isCreator ? 'creatorContestAlert' : 'alreadyJoinedAlert'
          }.message`
        ),
        withIcon: false,
      })

      return
    }

    if (contestData.contest.endDate < Date.now()) {
      setPopoverState({
        isOpen: true,
        header: t('contest.details.finishedContestAlert.header', {
          contest: contestData.contest?.name,
        }),
        message: t('contest.details.finishedContestAlert.message'),
        withIcon: false,
      })

      return
    } else if (contestData.contest.submissionDate > Date.now()) {
      setPopoverState({
        isOpen: true,
        header: t('contest.details.notStartedContestAlert.header', {
          contest: contestData.contest?.name,
        }),
        message: t('contest.details.notStartedContestAlert.message', {
          date: format(contestData.contest.submissionDate, 'dd.MM.yyyy HH:mm'),
        }),
        withIcon: false,
      })

      return
    }

    const result = await videoEditor({
      maximumDuration: contestData.contest.videoLengthLimit,
    })
    if (result.url) {
      setVideoDataUrl(result.url)
      setThumbnailUrl(result.thumbnailUrl || '')
      setThumbnailOffset(result.thumbnailOffset)
      setVideoDuration(result.duration)
      openContestJoinModal()
      Tracking.triggerEvent(
        TrackingEvent.ChallengeJoinStarted,
        trackChallengeProperties
      )
    }
  }

  const onVote = (contestId: UUID, videoId: UUID) =>
    history.push(`/contest/${contestId}/video/${videoId}`)

  const refetchAll = async () => {
    await refetch()
    refetchFirstPage()
  }

  const handleShareClick = useCallback(() => {
    Tracking.triggerEvent(TrackingEvent.ChallengeShared, {
      activityId: contestDetails?.activityId,
      contentId: contestDetails?.id,
      contentType: 'challenge',
      contentOwnerId: contestDetails?.creator?.user?.id,
      userId: user.id,
      timestamp: formatISO(new Date()),
    })
  }, [
    contestDetails?.activityId,
    contestDetails?.id,
    contestDetails?.creator?.user?.id,
    user.id,
  ])

  useEffect(() => {
    if (contestData?.contest?.name && contestId) {
      Tracking.triggerEvent(
        TrackingEvent.ChallengeViewed,
        trackChallengeProperties
      )
    }
    // ref: https://www.benmvp.com/blog/object-array-dependencies-react-useEffect-hook/
  }, [contestData?.contest?.name, contestId, trackChallengeProperties])

  const header = (
    <>
      <IonButtons slot="start">
        <BackButton defaultHref="/feed" />
      </IonButtons>
      <Title level={3}>
        {!isLoading && (
          <span className="block truncate">
            <span className="w-full">{contestData?.contest?.name}</span>
            <span className="flex text-xs font-normal whitespace-nowrap w-full -mt-1 child-color-strong">
              <span className="inline-block mr-1">
                {t('contest.details.createdBy')}
              </span>
              <IonRouterLink routerLink={`/@${contestData?.contest?.username}`}>
                <UserName
                  className="text-primary font-bold"
                  name={contestData?.contest?.creatorName}
                />
              </IonRouterLink>
            </span>
          </span>
        )}
      </Title>
      <IonButtons className="pr-2" slot="end">
        <ShareButton
          className="cursor-pointer color-black !mr-3"
          color="success"
          message={contestData?.contest?.name}
          buttonLabel={<Share color="success" />}
          url={`${appConfig.origin}/contest/${contestId}`}
          imageUrl={contestData?.contest?.creatorVideoThumbnail}
          feature="contest-details"
          params={{ refUsername: user?.username }}
          onClickShare={handleShareClick}
        />
        {contestDetails?.activityId && (
          <CommentsButton
            iconSlot="start"
            activityId={contestDetails?.activityId}
            commentsNumber={contestDetails?.commentsCount}
            commentsNumberPosition="right"
            channel={ContestSections.Details}
            page={Pages.Contest}
          />
        )}
        <LeaderboardButton
          className="!ml-4"
          isDisabled={
            !contestData?.contest?.participantsCount ||
            !!contestData?.contest?.hideVotesCount
          }
          color="success"
          contestId={contestId}
          isVoteCountHidden={contestData?.contest?.hideVotesCount}
        />
      </IonButtons>
    </>
  )

  if (isError) return null

  return (
    <Main
      header={header}
      refetch={refetchAll}
      headTitle={contestData?.contest?.name}
    >
      <video
        className="hidden"
        src={videoDataUrl}
        ref={videoRef}
        onLoadedMetadata={durationChangeHandler}
      />
      <Popover
        hasIcon={popoverState.withIcon}
        isOpen={popoverState.isOpen}
        onDismiss={() =>
          setPopoverState({
            isOpen: false,
            header: null,
            message: null,
            withIcon: false,
          })
        }
        headerMessage={popoverState.header}
      >
        <Trans>{popoverState.message}</Trans>
      </Popover>
      {!isLoadingContestDetails && !isLoading ? (
        <ContestDetails
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          participants={contestDetails?.participants}
          contest={{ ...contestData.contest, ...contestDetails.creator }}
          isJoined={isJoined}
          onJoinClick={onJoinContest}
          onVoteClick={onVote}
        />
      ) : (
        <Loading />
      )}
    </Main>
  )
}
